import { useState, useMemo } from 'react';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Clock,
  BarChart3,
  Target,
  AlertTriangle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import {
  usePipelineVelocity,
  useSalesForecast,
  useActivityTrends,
  useLostDealAnalysis,
} from '@/lib/hooks/leads/useReporting';
import { usePipelines } from '@/lib/hooks/leads/usePipelines';
import type {
  LeadsPipelineVelocityStage,
  LeadsActivityTrendPeriod,
  LeadsLostDealByReason,
  LeadsLostDealByStage,
  LeadsRecentLostDeal,
} from '@/lib/api';

function formatCurrency(value: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatNumber(value: number) {
  return new Intl.NumberFormat('en-US').format(value);
}

const ACTIVITY_TYPE_COLORS: Record<string, string> = {
  call: '#3b82f6',
  email: '#10b981',
  meeting: '#8b5cf6',
  note: '#f59e0b',
  task_completed: '#ef4444',
  stage_change: '#6b7280',
};

const ACTIVITY_TYPE_LABELS: Record<string, string> = {
  call: 'Calls',
  email: 'Emails',
  meeting: 'Meetings',
  note: 'Notes',
  task_completed: 'Tasks Completed',
  stage_change: 'Stage Changes',
};

function SkeletonTable({ rows = 5, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex gap-4">
          {Array.from({ length: cols }).map((_, j) => (
            <Skeleton key={j} className="h-8 flex-1" />
          ))}
        </div>
      ))}
    </div>
  );
}

function PipelineVelocityTab() {
  const { pipelines, loading: pipelinesLoading } = usePipelines();
  const defaultPipeline = useMemo(
    () => pipelines.find((p) => p.isDefault) ?? pipelines[0],
    [pipelines],
  );
  const [selectedPipelineId, setSelectedPipelineId] = useState<string | null>(null);

  const pipelineId = selectedPipelineId ?? defaultPipeline?.id ?? null;
  const { data, loading } = usePipelineVelocity(pipelineId);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <label className="text-sm font-medium text-muted-foreground">Pipeline</label>
        {pipelinesLoading ? (
          <Skeleton className="h-9 w-48" />
        ) : (
          <Select value={pipelineId ?? ''} onValueChange={(val) => setSelectedPipelineId(val)}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Select pipeline" />
            </SelectTrigger>
            <SelectContent>
              {pipelines.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Pipeline Velocity
          </CardTitle>
          <CardDescription>Average time and conversion rates per stage</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <SkeletonTable rows={5} cols={4} />
          ) : !data?.stages?.length ? (
            <p className="text-sm text-muted-foreground">No velocity data available.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="pb-2 font-medium">Stage</th>
                    <th className="pb-2 font-medium">Avg Days</th>
                    <th className="pb-2 font-medium">Deal Count</th>
                    <th className="pb-2 font-medium">Conversion Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {data.stages
                    .sort((a, b) => a.displayOrder - b.displayOrder)
                    .map((stage: LeadsPipelineVelocityStage) => (
                      <tr key={stage.stageId} className="border-b last:border-0">
                        <td className="py-3">
                          <div className="flex items-center gap-2">
                            <span
                              className="inline-block h-3 w-3 rounded-full"
                              style={{ backgroundColor: stage.stageColor }}
                            />
                            {stage.stageName}
                          </div>
                        </td>
                        <td className="py-3">{stage.avgDaysInStage.toFixed(1)}</td>
                        <td className="py-3">{formatNumber(stage.dealCount)}</td>
                        <td className="py-3">
                          <div className="flex items-center gap-2">
                            <div className="h-2 w-24 rounded-full bg-muted">
                              <div
                                className="h-2 rounded-full bg-primary"
                                style={{ width: `${Math.min(stage.conversionRate * 100, 100)}%` }}
                              />
                            </div>
                            <span className="text-muted-foreground">
                              {(stage.conversionRate * 100).toFixed(1)}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SalesForecastTab() {
  const { data, loading } = useSalesForecast();

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <Skeleton className="mb-2 h-4 w-24" />
                <Skeleton className="h-8 w-32" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <CardContent className="pt-6">
            <SkeletonTable rows={6} cols={3} />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) {
    return <p className="text-sm text-muted-foreground">No forecast data available.</p>;
  }

  const maxProjectedValue = Math.max(...data.monthlyProjections.map((p) => p.projectedValue), 1);

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Target className="h-4 w-4" />
              Weighted Pipeline
            </div>
            <p className="mt-1 text-2xl font-bold">{formatCurrency(data.weightedPipelineValue)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <DollarSign className="h-4 w-4" />
              Total Open Value
            </div>
            <p className="mt-1 text-2xl font-bold">{formatCurrency(data.summary.totalOpenValue)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <TrendingUp className="h-4 w-4" />
              Won Value
            </div>
            <p className="mt-1 text-2xl font-bold">{formatCurrency(data.summary.totalWonValue)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <BarChart3 className="h-4 w-4" />
              Avg Deal Size
            </div>
            <p className="mt-1 text-2xl font-bold">{formatCurrency(data.summary.avgDealSize)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Target className="h-4 w-4" />
              Win Rate
            </div>
            <p className="mt-1 text-2xl font-bold">{(data.summary.winRate * 100).toFixed(1)}%</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Monthly Projections
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!data.monthlyProjections.length ? (
            <p className="text-sm text-muted-foreground">No projections available.</p>
          ) : (
            <div className="space-y-3">
              {data.monthlyProjections.map((proj) => (
                <div key={proj.month} className="flex items-center gap-4">
                  <span className="w-24 shrink-0 text-sm font-medium">{proj.month}</span>
                  <div className="flex-1">
                    <div className="h-6 w-full rounded bg-muted">
                      <div
                        className="flex h-6 items-center rounded bg-primary px-2 text-xs text-primary-foreground"
                        style={{
                          width: `${(proj.projectedValue / maxProjectedValue) * 100}%`,
                          minWidth: proj.projectedValue > 0 ? '60px' : '0',
                        }}
                      >
                        {formatCurrency(proj.projectedValue)}
                      </div>
                    </div>
                  </div>
                  <span className="w-20 shrink-0 text-right text-sm text-muted-foreground">
                    {formatNumber(proj.dealCount)} deals
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ActivityTrendsTab() {
  const [period, setPeriod] = useState<'week' | 'month' | 'quarter'>('month');
  const { data, loading } = useActivityTrends(period);

  const activityTypes = useMemo(() => {
    if (!data?.periods?.length) return [];
    const types = new Set<string>();
    for (const p of data.periods) {
      for (const key of Object.keys(p.counts)) {
        types.add(key);
      }
    }
    return Array.from(types);
  }, [data]);

  const maxTotal = useMemo(() => {
    if (!data?.periods?.length) return 1;
    return Math.max(...data.periods.map((p) => p.total), 1);
  }, [data]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <label className="text-sm font-medium text-muted-foreground">Period</label>
        <Select
          value={period}
          onValueChange={(val) => setPeriod(val as 'week' | 'month' | 'quarter')}
        >
          <SelectTrigger className="w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="week">Weekly</SelectItem>
            <SelectItem value="month">Monthly</SelectItem>
            <SelectItem value="quarter">Quarterly</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Activity Trends
          </CardTitle>
          <CardDescription>Activity breakdown by period</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <SkeletonTable rows={6} cols={2} />
          ) : !data?.periods?.length ? (
            <p className="text-sm text-muted-foreground">No activity data available.</p>
          ) : (
            <div className="space-y-6">
              <div className="space-y-3">
                {data.periods.map((p: LeadsActivityTrendPeriod) => (
                  <div key={p.label} className="flex items-center gap-4">
                    <span className="w-28 shrink-0 text-sm font-medium">{p.label}</span>
                    <div className="flex-1">
                      <div
                        className="flex h-7 overflow-hidden rounded"
                        style={{
                          width: `${(p.total / maxTotal) * 100}%`,
                          minWidth: p.total > 0 ? '20px' : '0',
                        }}
                      >
                        {activityTypes.map((type) => {
                          const count = p.counts[type] ?? 0;
                          if (count === 0) return null;
                          return (
                            <div
                              key={type}
                              className="h-full"
                              style={{
                                backgroundColor: ACTIVITY_TYPE_COLORS[type] ?? '#9ca3af',
                                width: `${(count / p.total) * 100}%`,
                              }}
                              title={`${ACTIVITY_TYPE_LABELS[type] ?? type}: ${count}`}
                            />
                          );
                        })}
                      </div>
                    </div>
                    <span className="w-12 shrink-0 text-right text-sm text-muted-foreground">
                      {formatNumber(p.total)}
                    </span>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-4">
                {activityTypes.map((type) => (
                  <div key={type} className="flex items-center gap-2 text-sm">
                    <span
                      className="inline-block h-3 w-3 rounded"
                      style={{ backgroundColor: ACTIVITY_TYPE_COLORS[type] ?? '#9ca3af' }}
                    />
                    {ACTIVITY_TYPE_LABELS[type] ?? type}
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function LostDealAnalysisTab() {
  const { data, loading } = useLostDealAnalysis();

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          {Array.from({ length: 2 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <Skeleton className="mb-2 h-4 w-24" />
                <Skeleton className="h-8 w-32" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <CardContent className="pt-6">
            <SkeletonTable rows={4} cols={3} />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) {
    return <p className="text-sm text-muted-foreground">No lost deal data available.</p>;
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <AlertTriangle className="h-4 w-4" />
              Total Lost Deals
            </div>
            <p className="mt-1 text-2xl font-bold">{formatNumber(data.totalLost)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <TrendingDown className="h-4 w-4" />
              Total Lost Value
            </div>
            <p className="mt-1 text-2xl font-bold">{formatCurrency(data.totalLostValue)}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>By Reason</CardTitle>
        </CardHeader>
        <CardContent>
          {!data.byReason.length ? (
            <p className="text-sm text-muted-foreground">No data.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="pb-2 font-medium">Reason</th>
                  <th className="pb-2 font-medium">Count</th>
                  <th className="pb-2 font-medium">Total Value</th>
                </tr>
              </thead>
              <tbody>
                {data.byReason.map((r: LeadsLostDealByReason) => (
                  <tr key={r.reason} className="border-b last:border-0">
                    <td className="py-3">{r.reason || 'Unknown'}</td>
                    <td className="py-3">{formatNumber(r.count)}</td>
                    <td className="py-3">{formatCurrency(r.totalValue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>By Stage</CardTitle>
        </CardHeader>
        <CardContent>
          {!data.byStage.length ? (
            <p className="text-sm text-muted-foreground">No data.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="pb-2 font-medium">Stage</th>
                  <th className="pb-2 font-medium">Count</th>
                  <th className="pb-2 font-medium">Total Value</th>
                </tr>
              </thead>
              <tbody>
                {data.byStage.map((s: LeadsLostDealByStage) => (
                  <tr key={s.stageId} className="border-b last:border-0">
                    <td className="py-3">
                      <div className="flex items-center gap-2">
                        <span
                          className="inline-block h-3 w-3 rounded-full"
                          style={{ backgroundColor: s.stageColor }}
                        />
                        {s.stageName}
                      </div>
                    </td>
                    <td className="py-3">{formatNumber(s.count)}</td>
                    <td className="py-3">{formatCurrency(s.totalValue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent Lost Deals</CardTitle>
        </CardHeader>
        <CardContent>
          {!data.recentLostDeals.length ? (
            <p className="text-sm text-muted-foreground">No recent lost deals.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="pb-2 font-medium">Deal</th>
                    <th className="pb-2 font-medium">Value</th>
                    <th className="pb-2 font-medium">Reason</th>
                    <th className="pb-2 font-medium">Contact</th>
                    <th className="pb-2 font-medium">Company</th>
                    <th className="pb-2 font-medium">Close Date</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recentLostDeals.map((deal: LeadsRecentLostDeal) => (
                    <tr key={deal.id} className="border-b last:border-0">
                      <td className="py-3 font-medium">{deal.name}</td>
                      <td className="py-3">
                        {deal.value != null ? formatCurrency(deal.value) : '-'}
                      </td>
                      <td className="py-3">
                        {deal.lostReason ? (
                          <Badge variant="outline">{deal.lostReason}</Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="py-3">{deal.contactName ?? '-'}</td>
                      <td className="py-3">{deal.companyName ?? '-'}</td>
                      <td className="py-3">
                        {deal.actualCloseDate
                          ? new Date(deal.actualCloseDate).toLocaleDateString()
                          : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export function ReportsView() {
  return (
    <div className="px-6 py-4 space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Reports</h2>
        <p className="text-muted-foreground">Analytics and insights for your Leads data</p>
      </div>

      <Tabs defaultValue="velocity">
        <TabsList>
          <TabsTrigger value="velocity">
            <Clock className="mr-1.5 h-4 w-4" />
            Pipeline Velocity
          </TabsTrigger>
          <TabsTrigger value="forecast">
            <TrendingUp className="mr-1.5 h-4 w-4" />
            Sales Forecast
          </TabsTrigger>
          <TabsTrigger value="activity">
            <BarChart3 className="mr-1.5 h-4 w-4" />
            Activity Trends
          </TabsTrigger>
          <TabsTrigger value="lost">
            <AlertTriangle className="mr-1.5 h-4 w-4" />
            Lost Deal Analysis
          </TabsTrigger>
        </TabsList>

        <TabsContent value="velocity" className="mt-4">
          <PipelineVelocityTab />
        </TabsContent>
        <TabsContent value="forecast" className="mt-4">
          <SalesForecastTab />
        </TabsContent>
        <TabsContent value="activity" className="mt-4">
          <ActivityTrendsTab />
        </TabsContent>
        <TabsContent value="lost" className="mt-4">
          <LostDealAnalysisTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
